// impostazioni.ts — impostazioni pubbliche del server (chiavi app.*), lette all'avvio.
// Se il server non risponde si riprova da soli; la schermata mostra cosa succede.
import { useEffect, useSyncExternalStore } from "react";
import { richiesta, ErroreRete, impostaTimeout } from "./api.ts";
import { IMP_APP, RIPROVA_PREDEFINITA_SECONDI, TIMEOUT_RICHIESTA_PREDEFINITO_SECONDI } from "../costanti.ts";

export interface StatoImpostazioni {
  stato: "caricamento" | "pronte" | "errore";
  valori: Record<string, unknown>;
  versioneServer: string | null;
  errore: ErroreRete | null;
  riprovaTraSecondi: number;
}

let attuale: StatoImpostazioni = {
  stato: "caricamento",
  valori: {},
  versioneServer: null,
  errore: null,
  riprovaTraSecondi: RIPROVA_PREDEFINITA_SECONDI,
};
const ascoltatori = new Set<() => void>();
let timerRiprova: ReturnType<typeof setTimeout> | null = null;
let inCorso: Promise<void> | null = null;

function imposta(nuovo: Partial<StatoImpostazioni>): void {
  attuale = { ...attuale, ...nuovo };
  ascoltatori.forEach((a) => a());
}

export function leggiImpostazione<T>(chiave: string, ripiego: T, controllo: (v: unknown) => boolean): T {
  const v = attuale.valori[chiave];
  return controllo(v) ? (v as T) : ripiego;
}

export const eNumero = (v: unknown): boolean => typeof v === "number" && Number.isFinite(v);
export const eTesto = (v: unknown): boolean => typeof v === "string";
export const eBooleano = (v: unknown): boolean => typeof v === "boolean";
export const eElencoTesti = (v: unknown): boolean => Array.isArray(v) && v.every((x) => typeof x === "string");

export const leggiStatoImpostazioni = (): StatoImpostazioni => attuale;

/** `fresche` (v040): senza la copia del browser (per sapere se il server ha una versione nuova). */
export function caricaImpostazioni(fresche = false): Promise<void> {
  if (inCorso) return inCorso;
  if (timerRiprova) clearTimeout(timerRiprova);
  inCorso = (async () => {
    try {
      const r = await richiesta<{ versione: string; impostazioni: Record<string, unknown> }>(`/api/v1/impostazioni${fresche ? `?v=${Date.now()}` : ""}`);
      imposta({ stato: "pronte", valori: r.impostazioni, versioneServer: r.versione, errore: null });
      impostaTimeout(leggiImpostazione(IMP_APP.timeoutSecondi, TIMEOUT_RICHIESTA_PREDEFINITO_SECONDI, eNumero));
    } catch (e) {
      const errore = e instanceof ErroreRete ? e : new ErroreRete("server");
      const secondi = leggiImpostazione(IMP_APP.riprovaSecondi, RIPROVA_PREDEFINITA_SECONDI, eNumero);
      imposta({ stato: attuale.stato === "pronte" ? "pronte" : "errore", errore, riprovaTraSecondi: secondi });
      timerRiprova = setTimeout(() => void caricaImpostazioni(), secondi * 1000);
    } finally {
      inCorso = null;
    }
  })();
  return inCorso;
}

export function useImpostazioni(): StatoImpostazioni {
  useEffect(() => {
    const quandoTornaLaRete = () => void caricaImpostazioni();
    window.addEventListener("online", quandoTornaLaRete);
    return () => window.removeEventListener("online", quandoTornaLaRete);
  }, []);
  return useSyncExternalStore(
    (a) => {
      ascoltatori.add(a);
      return () => ascoltatori.delete(a);
    },
    () => attuale,
  );
}
